import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { buildPdfSandboxUrlFromParts } from '../utils/pdfSandboxUrl';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentMagnifyingGlassIcon,
  ShieldExclamationIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * RejectedFOIARulesReview Component
 *
 * Displays FOIA rules that were rejected during extraction validation,
 * allowing users to review, edit, approve, or dismiss them.
 *
 * Mirrors the SCG RejectedRulesReview component pattern.
 */
const RejectedFOIARulesReview = ({ rejectedRules, conflicts, onClose, sourceFile }) => {
  const [rules, setRules] = useState(rejectedRules || []);
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [editingRule, setEditingRule] = useState(null);
  const [approvedCount, setApprovedCount] = useState(0);
  const [dismissedCount, setDismissedCount] = useState(0);
  const [showConflicts, setShowConflicts] = useState(false);

  const queryClient = useQueryClient();

  // FOIA exemption options for the edit dropdown
  const exemptionOptions = [
    { value: '(b)(1)', label: '(b)(1) - National Security' },
    { value: '(b)(2)', label: '(b)(2) - Internal Personnel Rules' },
    { value: '(b)(3)', label: '(b)(3) - Statutory Exemptions' },
    { value: '(b)(4)', label: '(b)(4) - Trade Secrets' },
    { value: '(b)(5)', label: '(b)(5) - Deliberative Process' },
    { value: '(b)(6)', label: '(b)(6) - Personal Privacy' },
    { value: '(b)(7)', label: '(b)(7) - Law Enforcement' },
    { value: '(b)(7)(A)', label: '(b)(7)(A) - Ongoing Investigation' },
    { value: '(b)(7)(C)', label: '(b)(7)(C) - LE Personal Privacy' },
    { value: '(b)(7)(D)', label: '(b)(7)(D) - Confidential Sources' },
    { value: '(b)(7)(E)', label: '(b)(7)(E) - Investigative Techniques' },
    { value: '(b)(8)', label: '(b)(8) - Financial Institutions' },
    { value: '(b)(9)', label: '(b)(9) - Geological Information' },
    { value: 'General', label: 'General FOIA Guidance' },
  ];

  // Get exemption badge color
  const getExemptionColor = (exemption) => {
    if (!exemption) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (exemption.includes('(b)(1)')) return 'bg-red-100 text-red-800 border-red-300';
    if (exemption.includes('(b)(6)') || exemption.includes('(b)(7)(C)')) return 'bg-purple-100 text-purple-800 border-purple-300';
    if (exemption.includes('(b)(7)')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (exemption.includes('(b)(5)')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (exemption.includes('(b)(4)')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Get PDF viewer link for a rule
  const getPdfViewerLink = (rule) => {
    let pdfUrl = null;

    // Check citation_display first
    if (rule.citation_display?.pdf_url && rule.citation_display.pdf_url.startsWith('/api/')) {
      pdfUrl = rule.citation_display.pdf_url;
    }
    // Use file_hash from source_document
    else if (rule.source_document?.file_hash) {
      pdfUrl = `/api/v1/foia/pdf/${rule.source_document.file_hash}`;
    }
    // Direct file_hash on rule
    else if (rule.file_hash) {
      pdfUrl = `/api/v1/foia/pdf/${rule.file_hash}`;
    }

    if (!pdfUrl) return null;

    const page = rule.source_page || 1;

    // FOIA rule PDFs are a separate corpus (served from /api/v1/foia/pdf/)
    // — buildPdfSandboxUrlFromParts takes the already-resolved pdfUrl
    // directly rather than assuming the SCG pdf route, and the sandbox
    // viewer's Bearer-token fetch already covers /api/v1/foia/pdf/{hash}.
    // No ruleId: rejected rules aren't persisted yet.
    return buildPdfSandboxUrlFromParts({
      pdfUrl,
      page,
      title: rule.rule_title || 'FOIA Rule',
      q: rule.rule_description || rule.rule_title,
    });
  };

  // Approve mutation - sends approved rules to backend
  const approveMutation = useMutation({
    mutationFn: async (rulesToApprove) => {
      // R2-AUTH: endpoint now requires foia_process — attach the Bearer token
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/v1/foia/rules/rejected/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify(rulesToApprove)
      });
      if (!response.ok) throw new Error('Failed to approve rules');
      return response.json();
    },
    onSuccess: (data, variables) => {
      setApprovedCount(prev => prev + variables.length);
      queryClient.invalidateQueries(['foia-rules-search']);
      queryClient.invalidateQueries(['foia-sources']);
    },
    onError: (error) => {
      console.error('Failed to approve rules:', error);
    }
  });

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRules(newExpanded);
  };

  const handleApprove = (rule) => {
    // Clean up internal fields
    const ruleData = { ...rule };
    delete ruleData._rejection_reason;
    delete ruleData._needs_review;

    approveMutation.mutate([ruleData]);
    setRules(prev => prev.filter(r => r !== rule));
  };

  const handleDismiss = (rule) => {
    setRules(prev => prev.filter(r => r !== rule));
    setDismissedCount(prev => prev + 1);
  };

  const handleEdit = (rule, index) => {
    setEditingRule({ rule: { ...rule }, index });
  };

  const handleSaveEdit = () => {
    if (editingRule) {
      const newRules = [...rules];
      newRules[editingRule.index] = editingRule.rule;
      setRules(newRules);
      setEditingRule(null);
    }
  };

  const handleEditChange = (field, value) => {
    setEditingRule(prev => ({
      ...prev,
      rule: { ...prev.rule, [field]: value }
    }));
  };

  const handleApproveAll = () => {
    const allRules = rules.map(rule => {
      const ruleData = { ...rule };
      delete ruleData._rejection_reason;
      delete ruleData._needs_review;
      return ruleData;
    });
    approveMutation.mutate(allRules);
    setRules([]);
  };

  const handleDismissAll = () => {
    setDismissedCount(prev => prev + rules.length);
    setRules([]);
  };

  // Get validation warnings for a rule
  const getValidationWarnings = (rule) => {
    return rule._validation_warnings || [];
  };

  // Render completion state
  if (rules.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-green-900">FOIA Rules Review Complete</h3>
              <p className="text-sm text-green-700">
                {approvedCount} rule(s) approved, {dismissedCount} rule(s) dismissed
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-green-600 hover:text-green-800"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-yellow-900">
              {rules.length} FOIA Rule(s) Need Review
            </h3>
            <p className="text-sm text-yellow-700">
              These rules were flagged during extraction. Review and approve or dismiss them.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleApproveAll}
            disabled={approveMutation.isPending}
            className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            Approve All
          </button>
          <button
            onClick={handleDismissAll}
            className="flex items-center px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            <XMarkIcon className="w-4 h-4 mr-1" />
            Dismiss All
          </button>
          <button
            onClick={onClose}
            className="text-yellow-600 hover:text-yellow-800 p-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Conflicts Warning */}
      {conflicts && conflicts.length > 0 && (
        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <button
            onClick={() => setShowConflicts(!showConflicts)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <ShieldExclamationIcon className="w-5 h-5 text-orange-500 mr-2" />
              <span className="text-sm font-medium text-orange-800">
                {conflicts.length} potential rule conflict(s) detected
              </span>
            </div>
            {showConflicts ? (
              <ChevronUpIcon className="w-4 h-4 text-orange-500" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-orange-500" />
            )}
          </button>
          {showConflicts && (
            <div className="mt-3 space-y-2">
              {conflicts.map((conflict, idx) => (
                <div key={idx} className="text-xs text-orange-700 bg-orange-100 p-2 rounded">
                  <strong>{conflict.exemption}:</strong> "{conflict.rule1_title}" vs "{conflict.rule2_title}"
                  <div className="mt-1 text-orange-600">
                    {conflict.conflict_indicators?.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Progress */}
      {(approvedCount > 0 || dismissedCount > 0) && (
        <div className="mb-4 text-sm text-yellow-700">
          Progress: {approvedCount} approved, {dismissedCount} dismissed, {rules.length} remaining
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {rules.map((rule, index) => {
          const isExpanded = expandedRules.has(index);
          const isEditing = editingRule?.index === index;
          const warnings = getValidationWarnings(rule);

          return (
            <div
              key={index}
              className="bg-white border border-yellow-200 rounded-lg p-4"
            >
              {/* Rule Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getExemptionColor(rule.exemption_category)}`}>
                      {rule.exemption_category || 'Unknown Exemption'}
                    </span>
                    <button
                      onClick={() => toggleExpand(index)}
                      className="text-xs text-yellow-600 hover:text-yellow-800 flex items-center"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUpIcon className="w-3 h-3 mr-1" />
                          Less
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="w-3 h-3 mr-1" />
                          More
                        </>
                      )}
                    </button>
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingRule.rule.rule_title || ''}
                        onChange={(e) => handleEditChange('rule_title', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="Rule Title"
                      />
                      <textarea
                        value={editingRule.rule.rule_description || ''}
                        onChange={(e) => handleEditChange('rule_description', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        rows={2}
                        placeholder="Rule Description"
                      />
                      <textarea
                        value={editingRule.rule.redaction_criteria || ''}
                        onChange={(e) => handleEditChange('redaction_criteria', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        rows={2}
                        placeholder="Redaction Criteria"
                      />
                      <select
                        value={editingRule.rule.exemption_category || ''}
                        onChange={(e) => handleEditChange('exemption_category', e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Select Exemption</option>
                        {exemptionOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveEdit}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingRule(null)}
                          className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {rule.rule_title || 'No title specified'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {rule.rule_description || 'No description'}
                      </p>
                    </>
                  )}

                  {/* Rejection Reason */}
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      <span className="font-medium">Rejection:</span>
                      <span className="ml-1">{rule._rejection_reason || 'Unknown'}</span>
                    </div>

                    {/* View Source Link */}
                    {getPdfViewerLink(rule) && (
                      <Link
                        to={getPdfViewerLink(rule)}
                        target="_blank"
                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100"
                      >
                        <DocumentMagnifyingGlassIcon className="w-3 h-3 mr-1" />
                        View Source
                        {rule.source_page && (
                          <span className="ml-1 text-blue-500">(p. {rule.source_page})</span>
                        )}
                      </Link>
                    )}
                  </div>

                  {/* Validation Warnings */}
                  {warnings.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {warnings.map((warning, wIdx) => (
                        <div key={wIdx} className="flex items-start text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                          <InformationCircleIcon className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                          <span>{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && !isEditing && (
                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 space-y-2">
                      {rule.redaction_criteria && (
                        <div>
                          <span className="font-medium">Redaction Criteria:</span>
                          <p className="mt-0.5">{rule.redaction_criteria}</p>
                        </div>
                      )}
                      {rule.legal_citation && (
                        <div>
                          <span className="font-medium">Legal Citation:</span>
                          <p className="mt-0.5">{rule.legal_citation}</p>
                        </div>
                      )}
                      {rule.balancing_test && (
                        <div>
                          <span className="font-medium">Balancing Test:</span>
                          <p className="mt-0.5">{rule.balancing_test}</p>
                        </div>
                      )}
                      {rule.public_interest && (
                        <div>
                          <span className="font-medium">Public Interest:</span>
                          <p className="mt-0.5">{rule.public_interest}</p>
                        </div>
                      )}
                      {rule.examples && (
                        <div>
                          <span className="font-medium">Examples:</span>
                          <p className="mt-0.5">{rule.examples}</p>
                        </div>
                      )}
                      {rule.context_domain && (
                        <div>
                          <span className="font-medium">Domain:</span> {rule.context_domain}
                        </div>
                      )}
                      {rule.source && (
                        <div>
                          <span className="font-medium">Source:</span> {rule.source}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-1 ml-3">
                  <button
                    onClick={() => handleApprove(rule)}
                    disabled={approveMutation.isPending}
                    className="p-1.5 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
                    title="Approve Rule"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(rule, index)}
                    className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                    title="Edit Rule"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDismiss(rule)}
                    className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                    title="Dismiss Rule"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RejectedFOIARulesReview;
